import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { marked } from 'marked';
import roboIcon from './robo-icon.png';
import roboInit from './robot.gif';
import userIcon from './ual_cap.png';
import loadingIcon from './loading.gif';
import listeningGif from './listening.gif';
import happyGif from './happy.gif';
import sadGif from './sad.gif';

function App() {
    const [message, setMessage] = useState('');
    const [responses, setResponses] = useState([]);
    const [showChat, setShowChat] = useState(false);
    const [showUserTypeSelect, setShowUserTypeSelect] = useState(false);
    const [userType, setUserType] = useState(null);
    const [faqs, setFaqs] = useState([]);
    const [firstInteraction, setFirstInteraction] = useState(true);
    const [isRecording, setIsRecording] = useState(false);
    const [recognition, setRecognition] = useState(null);
    const [voices, setVoices] = useState([]);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [isVoiceSupported, setIsVoiceSupported] = useState(true);
    const [showFirefoxPopup, setShowFirefoxPopup] = useState(false);
    const messagesEndRef = useRef(null);

    const resetChat = () => {
        setMessage('');
        setResponses([]);
        setShowChat(false);
        setShowUserTypeSelect(false);
        setUserType(null);
        setFaqs([]);
        setFirstInteraction(true);
        setIsRecording(false);
        setIsSpeaking(false);
        setIsLoading(false);
        setIsListening(false);
    };

    useEffect(() => {
        const isFirefox = typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('firefox');

        if (isFirefox) {
            setIsVoiceSupported(false);
            setShowFirefoxPopup(true);
        } else {
            initializeSpeechRecognition();
        }
    }, []);

    const initializeSpeechRecognition = () => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            setIsVoiceSupported(false);
            return;
        }

        const recognitionInstance = new SpeechRecognition();
        recognitionInstance.lang = 'pt-PT';
        recognitionInstance.continuous = false;
        recognitionInstance.interimResults = false;

        recognitionInstance.onstart = () => {
            setIsRecording(true);
            setIsListening(true);
        };

        recognitionInstance.onresult = (event) => {
            const transcript = event.results[0][0].transcript;
            setIsRecording(false);
            setIsListening(false);
            sendMessage(transcript, true);
        };

        recognitionInstance.onend = () => {
            setIsRecording(false);
            setIsListening(false);
        };

        recognitionInstance.onerror = (event) => {
            console.error('Erro no reconhecimento de voz:', event.error);
            setIsRecording(false);
            setIsListening(false);
        };

        setRecognition(recognitionInstance);
    };

    useEffect(() => {
        scrollToBottom();
    }, [responses]);

    const decodeUnicode = (str) => {
        try {
            return decodeURIComponent(escape(str));
        } catch (error) {
            console.error('Erro ao decodificar a string:', error);
            return str;
        }
    };

    const handleFaqClick = (faq) => {
        sendMessage(decodeUnicode(faq), false);
        setFirstInteraction(false);
    };

    const renderMarkdown = (markdownText) => {
        if (typeof markdownText !== 'string') {
            console.error('Expected string input for markdown, but received:', typeof markdownText);
            return { __html: '' };
        }
        const htmlContent = marked(markdownText);
        const styledHtml = htmlContent
            .replace(/<h1>/g, '<h1 style="font-size: 20px; margin-bottom: 10px;">')
            .replace(/<h2>/g, '<h2 style="font-size: 18px; margin-bottom: 8px;">')
            .replace(/<h3>/g, '<h3 style="font-size: 16px; margin-bottom: 6px;">')
            .replace(/<h4>/g, '<h4 style="font-size: 14px; margin-bottom: 5px;">')
            .replace(/<table>/g, '<table style="border-collapse: collapse; width: 100%; border: 1px solid #ddd;">')
            .replace(/<th>/g, '<th style="border: 1px solid #ddd; padding: 8px;">')
            .replace(/<td>/g, '<td style="border: 1px solid #ddd; padding: 8px;">');

        return { __html: styledHtml };
    };

    const handleDislike = async (index) => {
        if (isSpeaking) {
            window.speechSynthesis.cancel();
        }
        const userMessage = responses[index].text;

        setResponses(prevResponses => {
            const updatedResponses = [...prevResponses];
            updatedResponses[index] = { ...updatedResponses[index], showReactions: false };
            return [
                ...updatedResponses,
                {
                    from: 'bot',
                    text: `<img src="${sadGif}" alt="Sad" style="width: 80px; margin-left: 80px;" />`,
                    icon: roboIcon
                }
            ];
        });

        scrollToBottom();

        setIsLoading(true);
        try {
            const result = await axios.post('https://chatbot.autonoma.pt/send_message', {
                message: userMessage,
                user_type: userType
            });
            const replyText = result.data.message.replace(/\*\*/g, '').replace(/##/g, '\n\n');

            setResponses(prevResponses => [
                ...prevResponses,
                { from: 'bot', text: replyText, icon: roboIcon, reaction: true, showReactions: true }
            ]);
        } catch (error) {
            console.error('Erro na chamada da API:', error);
            setResponses(prevResponses => [
                ...prevResponses,
                { from: 'bot', text: "Erro ao processar a mensagem, tente novamente.", icon: roboIcon }
            ]);
        } finally {
            setIsLoading(false);
            scrollToBottom();
        }
    };

    const pauseSpeech = () => {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
    };

    const handleLike = (index) => {
        if (isSpeaking) {
            window.speechSynthesis.cancel();
        }
        setResponses(prevResponses => {
            const updatedResponses = [...prevResponses];
            updatedResponses[index] = { ...updatedResponses[index], showReactions: false };
            return [
                ...updatedResponses,
                {
                    from: 'bot',
                    text: `<img src="${happyGif}" alt="Thankful" style="width: 80px; margin-left: 80px;" />`,
                    icon: roboIcon
                }
            ];
        });
        scrollToBottom();
    };

    const speakText = async (text) => {
        if (window.speechSynthesis.speaking) {
            window.speechSynthesis.cancel();
        }

        let detectedLanguage = await detectLanguageGoogle(text);
        let availableVoices = window.speechSynthesis.getVoices();
        let voiceSupported = availableVoices.some(voice => voice.lang.startsWith(detectedLanguage));

        let textToSpeak = text;
        let speechLanguage = detectedLanguage;

        if (!voiceSupported) {
            let warningMessage = "Hi! Sorry, but I don't support speech for this language yet. I'll say it in English.";

            let warningUtterance = new SpeechSynthesisUtterance(warningMessage);
            warningUtterance.lang = "en";
            window.speechSynthesis.speak(warningUtterance);

            await new Promise(resolve => (warningUtterance.onend = resolve));

            try {
                const translationResponse = await fetch(
                    `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${detectedLanguage}&tl=en&dt=t&q=${encodeURIComponent(text)}`
                );
                const translationData = await translationResponse.json();
                textToSpeak = translationData[0].map(item => item[0]).join(" ");
                speechLanguage = "en";
            } catch (error) {
                console.error("Erro ao traduzir para inglês:", error);
                textToSpeak = "We do not support this language, but here is the text in the original language.";
            }
        }

        const utterances = [];
        const splitSentences = textToSpeak.match(/[^.!?]+[.!?]*/g) || [textToSpeak];

        splitSentences.forEach(sentence => {
            let utterance = new SpeechSynthesisUtterance(sentence.trim());
            utterance.lang = speechLanguage;
            utterance.rate = 0.9;
            utterance.pitch = 1.0;

            let selectedVoice = availableVoices.find(voice => voice.lang.startsWith(speechLanguage) && voice.name.includes("Google")) ||
                                availableVoices.find(voice => voice.lang.startsWith(speechLanguage)) ||
                                availableVoices.find(voice => voice.lang.startsWith(speechLanguage.split('-')[0])) ||
                                availableVoices[0];

            if (selectedVoice) {
                utterance.voice = selectedVoice;
            }

            utterances.push(utterance);
        });

        function queueUtterances(utteranceList, index = 0) {
            if (index >= utteranceList.length) {
                setIsSpeaking(false);
                return;
            }
            let utterance = utteranceList[index];
            utterance.onend = () => queueUtterances(utteranceList, index + 1);
            window.speechSynthesis.speak(utterance);
        }

        setIsSpeaking(true);
        queueUtterances(utterances);
    };

    const handleSelectUserType = (type) => {
        setUserType(type);
        setShowUserTypeSelect(false);
        setShowChat(true);

        axios.get(`https://chatbot.autonoma.pt/get_faq?user_type=${type}`)
            .then(response => {
                const decodedFaqs = response.data.map(faq => decodeUnicode(faq));
                setFaqs(decodedFaqs);

                const greeting = 'Olá 👋, como posso ajudar? Deixo aqui algumas perguntas frequentes:';

                setResponses([
                    { from: 'bot', text: greeting, icon: roboIcon, showFaqButtons: true }
                ]);

                setFirstInteraction(false);
            })
            .catch(error => console.error('Erro ao buscar FAQs:', error));
    };

    const commonEnglishWords = new Set([
        "hello", "yes", "no", "thanks", "thank", "good", "bad", "please", "sorry",
        "ok", "hi", "bye", "morning", "afternoon", "evening", "night", "help", "what", "who", "when", "where", "why", "how"
    ]);

    const detectLanguageGoogle = async (text) => {
        try {
            const response = await fetch(`https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=pt&dt=t&q=${encodeURIComponent(text)}`);
            const data = await response.json();

            let detectedLang = "pt";

            if (data && Array.isArray(data)) {
                if (data[2]) {
                    detectedLang = data[2];
                } else if (data[0] && data[0][0] && data[0][0][1]) {
                    detectedLang = data[0][0][1];
                }
            }

            if (detectedLang === "pt" && commonEnglishWords.has(text.toLowerCase())) {
                return "en";
            }

            return detectedLang;
        } catch (error) {
            console.error("Erro ao detetar idioma:", error);
        }
        return "pt";
    };

    const startRecording = () => {
        if (isSpeaking) {
            window.speechSynthesis.cancel();
        }
        if (recognition && !isRecording) {
            recognition.start();
        } else {
            console.error('Reconhecimento de voz não está disponível ou já está sendo usado.');
        }
    };

    const sendMessage = async (text, autoSpeak = false) => {
        setMessage('');
        if (!text) return;
        setIsLoading(true);

        try {
            setResponses(prevResponses => [...prevResponses, { from: 'user', text, icon: userIcon }]);

            const result = await axios.post('https://chatbot.autonoma.pt/send_message', {
                message: text,
                user_type: userType
            });
            const replyText = result.data.message.replace(/\*\*/g, '').replace(/##/g, '\n\n');

            setResponses(prevResponses => [
                ...prevResponses,
                { from: 'bot', text: replyText, icon: roboIcon, showAudioControls: true, showFaqButtons: false, showReactions: true }
            ]);

            scrollToBottom();

            if (autoSpeak) {
                speakText(replyText);
            }
        } catch (error) {
            console.error('Erro na API:', error);
            setResponses(prevResponses => [
                ...prevResponses,
                { from: 'bot', text: "Erro ao processar a mensagem, tente novamente.", icon: roboIcon }
            ]);
        } finally {
            setIsLoading(false);
            setMessage('');
            scrollToBottom();
        }
    };

    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    useEffect(() => {
        const loadVoices = () => {
            const availableVoices = window.speechSynthesis.getVoices();
            setVoices(availableVoices);
        };
        loadVoices();
        window.speechSynthesis.onvoiceschanged = loadVoices;
    }, []);

    const handleStartChat = () => {
        setShowUserTypeSelect(true);
    };

    const handleFirefoxOk = () => {
        setShowFirefoxPopup(false);
    };

    const [showPrivacyModal, setShowPrivacyModal] = useState(false);
    const openPrivacyModal = () => setShowPrivacyModal(true);
    const closePrivacyModal = () => setShowPrivacyModal(false);

    const userTypeButtonStyle = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '30px 40px',
        backgroundColor: '#0474a4',
        color: 'white',
        border: '2px solid transparent',
        borderRadius: '15px',
        fontSize: '18px',
        cursor: 'pointer',
        minWidth: '180px',
        transition: 'transform 0.15s, border-color 0.15s'
    };

    return (
        <div style={{ fontFamily: 'Arial', backgroundColor: '#2c3e50', color: 'white', height: '100vh', display: 'flex', flexDirection: 'column' }}>

            <div style={{ position: 'absolute', top: '10px', right: '10px' }}>
                <button onClick={openPrivacyModal} style={{ backgroundColor: 'transparent', color: '#007bff', border: 'none', cursor: 'pointer', fontSize: '16px' }}>
                    Política de Privacidade
                </button>
            </div>

            {showPrivacyModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div style={{ backgroundColor: '#fff', color: '#333', padding: '20px', borderRadius: '10px', maxWidth: '600px', textAlign: 'left', overflowY: 'auto', maxHeight: '80%' }}>
                        <h2>Política de Privacidade</h2>
                        <p>Última atualização: 05 de Novembro de 2024</p>
                        <p>A Universidade Autónoma de Lisboa valoriza a privacidade dos utilizadores e compromete-se a proteger qualquer informação que possa ser partilhada durante a utilização do nosso chatbot. Esta política de privacidade descreve como o chatbot opera em termos de tratamento de dados e garante que a confidencialidade e a segurança dos utilizadores são mantidas em todos os momentos.</p>

                        <h3>1. Identidade da Organização</h3>
                        <p>A Universidade Autónoma de Lisboa é a entidade responsável pela operação deste chatbot, que é disponibilizado para auxiliar os utilizadores a obterem informações sobre a instituição, e os seus serviços, em tempo real. Para qualquer questão relacionada com esta política, os utilizadores podem entrar em contato através de <a href="mailto:mmira@autonoma.pt">mmira@autonoma.pt</a>.</p>

                        <h3>2. Dados Pessoais e Informação Coletada</h3>
                        <p>Este chatbot não armazena quaisquer dados pessoais ou qualquer outra informação de identificação dos utilizadores. Toda a interação ocorre em tempo real, processando as perguntas para gerar respostas, sem retenção de dados após a conclusão da conversa. Não são coletadas informações como nome, endereço de e-mail, número de telefone ou qualquer outro dado pessoal.</p>

                        <h3>3. Finalidade do Uso dos Dados</h3>
                        <p>O chatbot processa as interações apenas para responder às questões dos utilizadores em tempo real. Não existe qualquer utilização adicional ou secundária das informações transmitidas, seja para fins comerciais, de marketing ou de análise. As perguntas feitas pelos utilizadores são processadas temporariamente para gerar uma resposta relevante e personalizada. Após a interação, os dados são eliminados e não mantidos em nenhum sistema.</p>

                        <h3>4. Compartilhamento de Dados com Terceiros</h3>
                        <p>Como o chatbot não armazena informações, também não existe partilha de dados com terceiros. Na utilização de infraestruturas de serviços de terceiros, estes serviços são estritamente usados para o processamento das interações em tempo real, sem retenção ou partilha de dados após a interação.</p>

                        <h3>5. Segurança das Interações</h3>
                        <p>Para garantir a segurança e confidencialidade das interações, todas as comunicações entre o utilizador e o chatbot são protegidas por SSL/TLS (Secure Socket Layer / Transport Layer Security). Estas tecnologias de encriptação protegem as informações durante a transmissão, impedindo o acesso ou a interceção por terceiros não autorizados. Além disso, implementamos práticas e protocolos de segurança reconhecidos para proteger os dados processados temporariamente, garantindo que apenas as respostas solicitadas sejam geradas, sem acesso indevido ou manutenção de dados.</p>

                        <h3>6. Direitos dos Utilizadores</h3>
                        <p>De acordo com o regulamento geral de proteção de dados, os utilizadores têm o direito de aceder, retificar ou apagar os seus dados pessoais. No entanto, devido ao facto de que o chatbot não armazena informações pessoais ou outras informações após as interações, não é possível responder a solicitações relacionadas com acesso, retificação ou eliminação de dados, pois não são retidos dados que permitam o exercício desses direitos.</p>

                        <h3>7. Alterações à Política de Privacidade</h3>
                        <p>A Universidade Autónoma de Lisboa reserva-se o direito de atualizar esta política de privacidade conforme necessário para refletir mudanças nas operações do chatbot, sempre em conformidade com as leis de proteção de dados aplicáveis. A data da última atualização desta política encontra-se indicada no topo do documento.</p>

                        <h3>8. Contato</h3>
                        <p>Para mais informações sobre esta política de privacidade ou para esclarecer qualquer dúvida sobre o funcionamento do chatbot, os utilizadores podem entrar em contato com a Universidade Autónoma de Lisboa através do e-mail <a href="mailto:mmira@autonoma.pt">mmira@autonoma.pt</a>.</p>

                        <button onClick={closePrivacyModal} style={{
                            marginTop: '20px', padding: '10px 20px', backgroundColor: '#007bff', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer'
                        }}>
                            OK
                        </button>
                    </div>
                </div>
            )}

            {showFirefoxPopup && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
                    <div style={{ backgroundColor: '#2c3e50', padding: '20px', borderRadius: '10px', textAlign: 'center', maxWidth: '400px' }}>
                        <p style={{ color: 'white' }}>Este chatbot tem reconhecimento de voz, mas o teu navegador não o suporta. Vamos avançar com a funcionalidade de texto. Ok?</p>
                        <button onClick={handleFirefoxOk} style={{ padding: '10px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '5px', fontSize: '16px' }}>
                            OK
                        </button>
                    </div>
                </div>
            )}

            {!showChat && !showUserTypeSelect && !showFirefoxPopup ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '20px' }}>
                    <img src={roboInit} alt="UALbot" style={{ width: '100px', height: '100px' }} />
                    <h1>UALbot</h1>
                    <button onClick={handleStartChat} style={{ padding: '10px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '5px', fontSize: '16px' }}>
                        Começar a conversar
                    </button>
                </div>
            ) : showUserTypeSelect ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '20px' }}>
                    <img src={roboInit} alt="UALbot" style={{ width: '100px', height: '100px', marginBottom: '30px' }} />
                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }}>
                        <button
                            onClick={() => handleSelectUserType('aluno')}
                            style={userTypeButtonStyle}
                            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = '#fff'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}
                        >
                            <span style={{ fontSize: '48px', marginBottom: '8px' }}>🎓</span>
                            <span>Sou Aluno</span>
                        </button>
                        <button
                            onClick={() => handleSelectUserType('docente')}
                            style={userTypeButtonStyle}
                            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = '#fff'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}
                        >
                            <span style={{ fontSize: '48px', marginBottom: '8px' }}>👨‍🏫</span>
                            <span>Sou Docente</span>
                        </button>
                    </div>
                    <button
                        onClick={() => setShowUserTypeSelect(false)}
                        style={{ marginTop: '30px', padding: '8px 16px', backgroundColor: 'transparent', color: '#bdc3c7', border: '1px solid #7f8c8d', borderRadius: '5px', fontSize: '14px', cursor: 'pointer' }}
                    >
                        ← Voltar
                    </button>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <div style={{ background: 'linear-gradient(to left, #00334e, #005682)', padding: '10px', textAlign: 'left', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', borderRadius: '0 0 15px 15px' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <img src={roboIcon} alt="UALbot" style={{ width: '50px', height: '50px', marginRight: '10px' }} />
                            <div>
                                <h1 style={{ margin: 0 }}>UALbot</h1>
                                <div style={{ color: '#fff', fontSize: '14px' }}>
                                    🟢 Online · {userType === 'docente' ? '👨‍🏫 Docente' : '🎓 Aluno'}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto', padding: '10px', paddingBottom: '80px', backgroundColor: '#2c3e50' }}>
                        {responses && responses.map((resp, index) => (
                            <div key={index} style={{ marginBottom: '20px' }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: resp.from === 'user' ? 'flex-end' : 'flex-start',
                                    marginBottom: '10px'
                                }}>
                                    <img src={resp.icon} alt="Icon" style={{ width: '50px', height: '50px', marginRight: '10px' }} />
                                    <div style={{
                                        backgroundColor: resp.from === 'bot' ? '#0474a4' : '#7f8c8d',
                                        padding: '10px',
                                        borderRadius: '20px',
                                        maxWidth: '60%',
                                        minHeight: '60px'
                                    }}>
                                        <p dangerouslySetInnerHTML={renderMarkdown(resp.text)} />

                                        {resp.showFaqButtons && faqs.length > 0 && (
                                            <div style={{ paddingTop: '10px' }}>
                                                {faqs.map((faq, faqIndex) => (
                                                    <button key={faqIndex} onClick={() => handleFaqClick(faq)} style={{
                                                        backgroundColor: '#007bff',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '20px',
                                                        padding: '10px 20px',
                                                        marginBottom: '10px',
                                                        marginRight: '5px',
                                                        cursor: 'pointer',
                                                        display: 'inline-block',
                                                        fontSize: '14px',
                                                    }}>
                                                        {faq}
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                        {resp.from === 'bot' && resp.showReactions && (
                                            <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginTop: '10px' }}>
                                                <span style={{ fontSize: '20px', cursor: 'pointer', marginRight: '10px' }} onClick={() => handleLike(index)}>👍</span>
                                                <span style={{ fontSize: '20px', cursor: 'pointer' }} onClick={() => handleDislike(index)}>👎</span>
                                            </div>
                                        )}

                                        {resp.from === 'bot' && resp.showAudioControls && (
                                            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                                <button onClick={() => speakText(resp.text)} style={{ fontSize: '15px', cursor: 'pointer', background: 'none', border: 'none', color: 'white' }}>🔊 </button>
                                                <button onClick={pauseSpeech} style={{ fontSize: '15px', cursor: 'pointer', background: 'none', border: 'none', color: 'white' }}>⏸️</button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}

                        {isLoading && (
                            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                <img src={loadingIcon} alt="A pensar..." style={{ width: '100px', height: '100px' }} />
                            </div>
                        )}

                        {isListening && (
                            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                <img src={listeningGif} alt="A ouvir..." style={{ width: '100px', height: '100px' }} />
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    <div style={{ padding: '10px', backgroundColor: '#34495e', display: 'flex', alignItems: 'center', position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1 }}>
                        <button
                            onClick={resetChat}
                            title="Trocar de perfil"
                            style={{ background: 'transparent', color: '#bdc3c7', border: '1px solid #7f8c8d', borderRadius: '20px', padding: '8px 14px', fontSize: '13px', cursor: 'pointer', marginRight: '10px', whiteSpace: 'nowrap' }}
                        >
                            ↺ Trocar de perfil
                        </button>
                        <input
                            type="text"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    sendMessage(message);
                                }
                            }}
                            placeholder="Digite a sua mensagem aqui..."
                            style={{ flexGrow: 1, padding: '10px', borderRadius: '5px', border: 'none', backgroundColor: '#95a5a6', marginRight: '10px', color: 'white' }}
                        />
                        <button onClick={() => sendMessage(message)} style={{ backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '20px', padding: '10px 20px' }}>
                            ⬆️
                        </button>
                        {isVoiceSupported && (
                            <button onClick={startRecording} style={{ backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '20px', padding: '10px 20px', marginLeft: '10px' }}>
                                {isRecording ? '' : '🎙️ '}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default App;